<template>
  <Modal
    :value="value"
    :hasFooter="true"
    :warning="warningText"
    width="auto"
    minWidth="961px"
    subtitle="Confira os dados que serão atualizados"
    ref="modal"
    class="modal-component"
    @modal-confirm="confirmModal"
    @modal-cancel="cancelModal"
  >
    <template #body-content>
      <div
        v-if="absoluteRatesRows.length > 0 && formFields.dates.length > 0"
        class="body-content"
      >
        <!-- CABECALHO -->
        <div class="body-content__heading">
          <PmwHeading
            class="body-content__heading body-content__heading--title"
            level="1"
          >
            {{ hotel.nome }}
          </PmwHeading>
          <div class="body-content__heading body-content__heading--subtitle">
            <PmwHeading level="2">
              Quais valores serão atualizados?
            </PmwHeading>
          </div>
        </div>

        <div v-if="formFields" class="mb-3">
          <Chip
            v-for="(date, i) in formFields.dates"
            :key="`${date.start}-${date.end}-${i}`"
          >
            {{ formatDate(new Date(date.rawStart), 'dd MMM yyyy') }} -
            {{ formatDate(new Date(date.rawEnd), 'dd MMM yyyy') }}
          </Chip>
        </div>
      </div>

      <SimpleTable
        v-if="
          formFields &&
            formFields.dates.length > 0 &&
            absoluteRatesRows.length > 0 &&
            absoluteRatesHeaders.length > 0
        "
        :columns="absoluteRatesHeaders"
        :rows="absoluteRatesRows"
        :numFormat="true"
        :ignoreNumFormatCols="['roomCode']"
        class="simple-table"
      >
        <template #roomCode="{column}">
          <td class="justify-content-start">{{ column.text }}</td>
        </template>

        <template #row-roomCode="{ rowItem }">
          <td class="text-left flex-room ">
            <div class="simple-table--room">
              <span class="font-weight-bold">{{ rowItem['roomCode'] }} - </span>
            </div>
            <span class="font-weight-normal simple-table--base">
              {{ rowItem['selectedWeekDays'] }}
            </span>
          </td>
        </template>

        <template
          v-for="pax in paxSlots"
          v-slot:[`row-${pax.value}`]="{ rowItem }"
        >
          <td :key="pax.value" class="simple-table--cell">
            <span v-if="!rowItem[pax.value] && +rowItem[pax.value] !== 0"
              >-</span
            >
            <span v-else>{{ rowItem[pax.value] }} Pontos</span>
          </td>
        </template>
      </SimpleTable>
    </template>

    <template #actions>
      <!-- CANCELAR -->
      <PmwButton
        size="large"
        variation="blue-100"
        layout="outline"
        class="modal-component--content-button"
        @click="cancelModal"
        >Cancelar</PmwButton
      >

      <!-- CONFIRMAÇÃO -->
      <PmwButton
        size="large"
        variation="blue-100"
        class="modal-component--content-button"
        @click="confirmModal"
      >
        Confirmar
      </PmwButton>
    </template>
  </Modal>
</template>

<script>
import { mapGetters } from 'vuex'
import Modal from '@/components/_base/patterns/Modal'
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import Chip from '@/components/_base/patterns/Chip'
import SimpleTable from '@/components/_base/patterns/SimpleTable'
import PmwButton from '@solucoes/components-lib-tmp/src/components/elements/PmwButton'
import hotelRatesConfirmationModalMixin from '@/mixins/hotelRatesConfirmationModalMixin'

/**
 * Componente para apresentar a modal de confirmação da tarifa absoluta e suas restrições
 */
export default {
  name: 'PointsConfirmationModal',
  mixins: [hotelRatesConfirmationModalMixin],
  components: {
    Modal,
    PmwHeading,
    Chip,
    SimpleTable,
    PmwButton
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    formFields: {
      type: Object,
      default: () => null
    },
    rooms: {
      type: Array,
      default: () => []
    },
    selectedGuestsOptions: {
      type: Array,
      default: () => []
    },
    /** Dados do hotel */
    hotel: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      absoluteRatesHeaders: [],
      absoluteRatesRows: [],
      /** Texto de aviso para quando existirem valores 0 */
      warningText: ''
    }
  },
  computed: {
    ...mapGetters('hotel', ['getCurrency', 'getCurrencySymbol']),
    /**
     * Retorna uma lista filtrada dos cabeçalhos de taxas absolutas, excluindo aqueles com os valores "roomCode" e "desconto".
     */
    paxSlots() {
      return this.absoluteRatesHeaders.filter(
        (item) => item.value !== 'roomCode' && item.value !== 'desconto'
      )
    }
  },
  watch: {
    value: {
      handler(newValue) {
        if (newValue) {
          this.loadingData()
        } else {
          this.warningText = ''
        }
      }
    }
  },
  methods: {
    cancelModal() {
      this.$emit('cancelModal')
      /** Fecha a modal */
      this.$emit('input', false)
    },
    confirmModal() {
      this.$emit('confirmModal')
      /** Fecha a modal */
      this.$emit('input', false)
    },
    loadingData() {
      /**
       * monta tabela com dados atualizados da
       * tarifa absoluta para popular a modal de confirmação
       */
      if (this.formFields) {
        const rows = []
        this.formFields.editedRates.forEach((editedRate) => {
          editedRate.rules.forEach((rule) => {
            rows.push(rule)
          })
        })

        if (rows.length > 0) {
          rows.forEach((row) => {
            /** Remove os valores que são nulos */
            Object.getOwnPropertyNames(row).forEach((val) => {
              if (row[val] === null) {
                delete row[val]
              }

              /** Se algum valor for 0 seta o aviso */
              if (row[val] === 0 && !(typeof row.desconto !== 'undefined')) {
                this.warningText = `Você está cadastrando valores <strong>${this.getCurrencySymbol} 0,00</strong> em alguns quartos.`
              }
            })

            if (row.selectedWeekDays.length > 0) {
              row.selectedWeekDays = this.formatArrayToString(
                row.selectedWeekDays.map((week) => week.text.slice(0, 3))
              )
            }

            row.roomCode = this.formatArrayToString(
              this.rooms
                .filter((g) => row.roomCode === g.codigo)
                .flatMap((g) => g.nome)
            )
          })

          /** Filtra apenas os com dias e valores selecionados */
          this.absoluteRatesRows = rows.filter(
            (item) =>
              item.selectedWeekDays.length > 0 && Object.keys(item).length > 2
          )

          this.absoluteRatesHeaders = []

          const headers = [...this.selectedGuestsOptions]

          this.absoluteRatesHeaders.push({ text: 'Quartos', value: 'roomCode' })

          headers.forEach((item) => {
            this.absoluteRatesHeaders.push({
              text: item.text,
              value: item.value
            })
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.simple-table {
  .discount {
    color: $error;
  }

  &--cell {
    padding: 0.8em 0;
  }

  &--column {
    width: 100%;
    display: flex;
    padding: 0;
    justify-content: flex-end;
  }

  &--room {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: max-content;
    margin-right: 5px;
    color: $gray-darkest;
  }

  &--content {
    &-button {
      width: 160px;

      & + & {
        margin-left: 12px;
      }
    }
  }

  .flex-room {
    display: flex;
    white-space: nowrap;
  }
}

.body-content {
  padding: 35px 35px 20px 35px;

  &__heading {
    margin-bottom: 20px;
    &--title {
      font-size: font-size('size-x-large');
      margin-bottom: 32px;
    }
    &--subtitle {
      display: flex;
      align-items: center;
      gap: 8px;

      h2,
      p {
        margin-bottom: 0px;
      }
    }
  }
}
</style>
